import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import { articles, categories, siteConfig } from '@/mocks/data';

const articleImages: Record<string, string> = {
  'best-free-movie-sites-2025': 'https://readdy.ai/api/search-image?query=Cinematic%20film%20reel%20and%20movie%20posters%20displayed%20on%20a%20warm%20lit%20wooden%20shelf%2C%20cozy%20home%20theater%20vibe%20with%20soft%20golden%20ambient%20light%2C%20rich%20dark%20wood%20textures%2C%20film%20noir%20aesthetic%20with%20warm%20amber%20and%20deep%20crimson%20accents%2C%20elegant%20cinematic%20atmosphere&width=1200&height=600&seq=art-full-01&orientation=landscape',
  'how-to-choose-streaming-site': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20living%20room%20with%20large%20flat%20screen%20TV%20displaying%20vibrant%20movie%20scene%2C%20warm%20evening%20ambient%20lighting%20from%20floor%20lamp%2C%20comfortable%20sofa%20with%20throw%20pillows&width=1200&height=600&seq=art-full-02&orientation=landscape',
  'top-korean-drama-sites': 'https://readdy.ai/api/search-image?query=Elegant%20Korean%20drama%20scene%20aesthetic%20with%20warm%20romantic%20lighting%2C%20cherry%20blossom%20petals%20in%20soft%20focus%2C%20traditional%20hanok%20house%20background%20with%20modern%20touches&width=1200&height=600&seq=art-full-03&orientation=landscape',
  'anime-streaming-guide': 'https://readdy.ai/api/search-image?query=Vibrant%20anime%20art%20style%20illustration%20with%20colorful%20characters%20and%20dynamic%20action%20poses%2C%20Japanese%20manga%20inspired%20backdrop%20with%20cherry%20blossoms%2C%20warm%20sunset%20color%20palette&width=1200&height=600&seq=art-full-04&orientation=landscape',
  'documentary-must-watch': 'https://readdy.ai/api/search-image?query=Stunning%20nature%20documentary%20scene%20with%20majestic%20mountain%20landscape%20at%20golden%20hour%2C%20wildlife%20in%20natural%20habitat%2C%20professional%20cinematography%20aesthetic%20with%20warm%20earth%20tones&width=1200&height=600&seq=art-full-05&orientation=landscape',
  'variety-show-2025': 'https://readdy.ai/api/search-image?query=Lively%20TV%20variety%20show%20stage%20with%20colorful%20lighting%20and%20dynamic%20set%20design%2C%20entertaining%20performance%20atmosphere%20with%20warm%20spotlights&width=1200&height=600&seq=art-full-06&orientation=landscape',
  'mobile-movie-watching-tips': 'https://readdy.ai/api/search-image?query=Smartphone%20displaying%20movie%20playback%20on%20screen%20in%20cozy%20bedroom%20with%20warm%20bedside%20lamp%20lighting%2C%20headphones%20and%20popcorn%20nearby&width=1200&height=600&seq=art-full-07&orientation=landscape',
  'classic-movie-collection': 'https://readdy.ai/api/search-image?query=Vintage%20film%20collection%20display%20with%20classic%20movie%20posters%20in%20ornate%20gold%20frames%2C%20old%20film%20projector%20on%20wooden%20table%2C%20warm%20nostalgic%20lighting&width=1200&height=600&seq=art-full-08&orientation=landscape',
  'free-vs-vip-comparison': 'https://readdy.ai/api/search-image?query=Split%20comparison%20scene%20showing%20two%20contrasting%20viewing%20experiences%2C%20premium%20home%20theater%20versus%20cozy%20laptop%20setup%2C%20warm%20ambient%20lighting&width=1200&height=600&seq=art-full-09&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.id === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-900 font-heading mb-3">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryName = categories.find((c) => c.id === article.category)?.name || '分类';
  const categoryPath = `/category/${article.category}`;

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteConfig.url}/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: siteConfig.url,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: `${siteConfig.url}${categoryPath}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`${siteConfig.url}/article/${article.id}`}
        ogType="article"
        schema={{ '@graph': [articleSchema, breadcrumbSchema] }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName, href: categoryPath },
              { label: article.title },
            ]}
          />

          <article className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <div className="mb-6 md:mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {categoryName}
                  </span>
                  {article.tags.slice(0, 2).map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading leading-tight mb-4">
                  {article.title}
                </h1>

                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i> {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i> {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i> {article.readTime}
                  </span>
                  <span className="flex items-center gap-1.5 text-accent-600">
                    <i className="ri-star-fill"></i> {article.rating}
                  </span>
                </div>
              </div>

              <div className="relative h-64 md:h-80 rounded-2xl overflow-hidden mb-8 md:mb-10">
                <img
                  src={articleImages[article.id]}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-lg max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                  prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                  prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-strong:text-foreground-900
                  prose-ul:my-4 prose-li:text-foreground-700 prose-li:leading-relaxed
                  prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {relatedArticles.length > 0 && (
                <div className="mt-12 pt-8 border-t border-background-200/70">
                  <h3 className="text-xl font-bold text-foreground-900 font-heading mb-6">
                    相关推荐
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/article/${ra.id}`}
                        className="group cursor-pointer"
                      >
                        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all">
                          <div className="h-36 overflow-hidden">
                            <img
                              src={articleImages[ra.id]}
                              alt={ra.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              loading="lazy"
                            />
                          </div>
                          <div className="p-3">
                            <h4 className="text-sm font-semibold text-foreground-900 font-heading line-clamp-2 group-hover:text-primary-600 transition-colors">
                              {ra.title}
                            </h4>
                            <p className="text-xs text-foreground-500 mt-1">{ra.date}</p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}